import _ from "lodash";
import moment from "moment";
import {
  orderBy,
  where,
  doc,
  getDoc,
  limit,
  documentId,
} from "firebase/firestore";

import FirestoreDocument from "./document";
import { db } from "../../config/firebase";
import Logger from "utils/logger";
import CurrentSession from "data/userSession";
import AnalyticsService from "./analytics";
import { NOTIFICATION_TYPES } from "../../config/notifications";

export default class Notification extends FirestoreDocument {
  static COLLECTION = "notifications";

  constructor(id = null, data = {}) {
    super();

    this.id = id;
    this.date = moment(_.get(data, "date", moment()));
    this.from = _.get(data, "from", { uid: null, username: null });
    this.to = _.get(data, "to", { uid: null, username: null });
    this.message = _.get(data, "message", null);
    this.notified = _.get(data, "notified", {});
    this.type = _.get(data, "type", NOTIFICATION_TYPES.KUDOS); // "kudos", "nudge", or "buddies"
    this.read = _.get(data, "read", false);
    this.uid = _.get(data, "uid", CurrentSession.user.uid);

    this.saveCheckpoint();
  }

  static async fromID(ID) {
    const placeholder = new Notification(ID);
    const { id, data } = await placeholder.fetch();

    if (!id || !data) {
      throw new Error(`Notification not found for ID: ${ID}`);
    }
    return new Notification(id, data);
  }

  static async encourage(from, to, type = "kudos", message = null) {
    // Validate required parameters
    if (!from || !to || !from.uid || !to.uid) {
      throw new Error("Missing required parameters for notification");
    }

    if (
      !_.includes([NOTIFICATION_TYPES.KUDOS, NOTIFICATION_TYPES.NUDGE], type)
    ) {
      throw new Error("Invalid notification type. Must be 'kudos' or 'nudge'");
    }

    // Create custom document ID: "YYYY-MM-DD uidFrom uidTo"
    const today = moment().format("YYYY-MM-DD");
    const customId = `${today} ${from.uid} ${to.uid}`;

    // Create the notification with implicit values
    const notification = new Notification(customId);
    notification.date = moment(); // Current date/time
    notification.from = from;
    notification.to = to;
    notification.message = message?.trim() || null; // Use the provided message or null
    notification.notified = {};
    notification.type = type;
    notification.uid = from.uid; // Set the recipient as the document owner

    // Save to Firestore
    await notification.save();

    // Track analytics event
    AnalyticsService.event(`accountability_${type}`, {
      notification_type: type,
      from_uid: from.uid,
      to_uid: to.uid,
      has_message: !!message,
    });

    Logger.debug(
      `[FS-N] Notification sent: ${type} from ${from.username} to ${
        to.username
      }${message ? ` with message: "${message}"` : ""}`
    );

    return notification;
  }

  static async getForUser(uid, limitCount = 10) {
    const docsFrom = await FirestoreDocument.queryForDocs(
      Notification.COLLECTION,
      [
        where("type", "in", [
          NOTIFICATION_TYPES.KUDOS,
          NOTIFICATION_TYPES.NUDGE,
        ]),
        where("from.uid", "==", uid),
        where("test", "==", false),
        orderBy("date", "desc"),
        limit(limitCount),
      ],
      `sent from ${uid}`
    );
    const docsTo = await FirestoreDocument.queryForDocs(
      Notification.COLLECTION,
      [
        where("type", "in", [
          NOTIFICATION_TYPES.KUDOS,
          NOTIFICATION_TYPES.NUDGE,
        ]),
        where("to.uid", "==", uid),
        where("test", "==", false),
        orderBy("date", "desc"),
        limit(limitCount),
      ],
      `sent to ${uid}`
    );

    return _.map(
      [...docsFrom, ...docsTo],
      (d) => new Notification(d.id, d.data())
    );
  }

  static async getUnreadForUser(uid) {
    const docs = await FirestoreDocument.queryForDocs(
      Notification.COLLECTION,
      [
        where("uid", "==", uid),
        where("read", "==", false),
        orderBy("date", "desc"),
      ],
      `unread notifications for ${uid}`
    );

    return _.map(docs, (d) => new Notification(d.id, d.data()));
  }

  // The set of uids `fromUid` has sent kudos/nudges to today, as ONE query
  // instead of a getDoc per card. Doc ids are "YYYY-MM-DD from to", so a
  // documentId() prefix range over "today from " returns exactly today's sends.
  // Routed through the memoized+monitored queryForDocs, so the N Kudos buttons on
  // a feed/community page share a single fetch (and it resets when a send saves).
  static async sentToday(fromUid) {
    const today = moment().format("YYYY-MM-DD");
    const prefix = `${today} ${fromUid} `;
    const docs = await FirestoreDocument.queryForDocs(
      Notification.COLLECTION,
      [
        where(documentId(), ">=", prefix),
        where(documentId(), "<", `${prefix}`),
      ],
      `sent ${today} ${fromUid}`
    );
    const set = new Set();
    docs.forEach((d) => {
      const parts = d.id.split(" ");
      if (parts.length === 3) set.add(parts[2]);
    });
    return set;
  }

  static async checkIfSent(fromUid, toUid) {
    const today = moment().format("YYYY-MM-DD");
    const customId = `${today} ${fromUid} ${toUid}`;

    // TODO check cache
    try {
      // Check if document exists with this ID
      const docRef = doc(db, Notification.COLLECTION, customId);
      const docSnap = await getDoc(docRef);

      return docSnap.exists();
    } catch (error) {
      Logger.error("[FS-N] Error checking if notification sent:", error);
      return false;
    }
  }

  async markAsRead() {
    this.read = true;
    await this.save();

    // Track analytics event
    AnalyticsService.event("notification_read", {
      notification_id: this.id,
      notification_type: this.type,
      from_uid: this.from.uid,
      to_uid: this.to.uid,
    });

    Logger.debug(`[FS-N] Notification marked as read: ${this.id}`);
  }

  async markAsUnread() {
    this.read = false;
    await this.save();

    Logger.debug(`[FS-N] Notification marked as unread: ${this.id}`);
  }

  get json() {
    return {
      date: this.date.toISOString(),
      dateUTC: moment(this.date).utc().toISOString(),
      from: this.from,
      to: this.to,
      message: this.message,
      notified: this.notified,
      type: this.type,
      read: this.read,
      uid: this.uid,
    };
  }

  get displayText() {
    const fromName = this.from.username || "A fellow tugger";

    switch (this.type) {
      case NOTIFICATION_TYPES.KUDOS:
        return this.message
          ? `${fromName} sent you kudos: "${this.message}"`
          : `${fromName} sent you kudos!`;
      case NOTIFICATION_TYPES.NUDGE:
        return this.message
          ? `${fromName} sent you encouragement: "${this.message}"`
          : `${fromName} sent you encouragement!`;
      case NOTIFICATION_TYPES.BUDDIES:
        return this.message;
      default:
        return this.message;
    }
  }

  get isFromCurrentUser() {
    return this.from.uid === CurrentSession.user.uid;
  }

  get isToCurrentUser() {
    return this.to.uid === CurrentSession.user.uid;
  }
}
